import React, { ReactElement, ReactNode, forwardRef, useRef } from "react";
import {
  AutocompleteTypes,
  OptionType,
} from "./tag-input-with-autocomplete.types";
import { Term } from "../tag-input-with-autocomplete/tag-input-with-autocomplete.types";
import classnames from "classnames";
import { useCombinedRefs } from "../utils/useCombinedRefs";
import uniqueId from "lodash.uniqueid";
import {
  handleTransformTerm,
  transformItem,
} from "./tag-input-with-autocomplete.util";
import Icon from "../icon";
import { IconSize } from "../types";

/**
 * Wraps matching array indexs (characters) from search value with <mark> tag
 * Ex: Search value: "b"
 * Ex: ["<mark>B</mark>", "ahamas"]
 */
const simpleItem = (
  itemPieces: string[],
  inputValue: string
): string | ReactNode => {
  return (itemPieces as string[])?.map((value: string, idx: number) => {
    if (value.toLowerCase() === inputValue.toLowerCase()) {
      return <mark key={`item${idx}`}>{value}</mark>;
    } else {
      return value;
    }
  });
};

/**
 * Iterates over object props (key, value) to wrap matching array indexs (characters) from search value with <mark> tag
 * Ex: Search value: "b"
 * Ex: {key: ["<mark>B</mark>", "S"], value: ["<mark>B</mark>", "ahamas"], metadata: "example"}
 */
const customItem = (itemPieces: OptionType, inputValue: string): OptionType => {
  const result = Object.entries(itemPieces).reduce(
    (newObj, [key, value]): OptionType => {
      if (Array.isArray(value) && key !== "metadata") {
        const formatPiece = value.map((val: string, idx: any) => {
          if (val.toLowerCase() === inputValue.toLowerCase()) {
            return <mark key={`item${idx}`}>{val}</mark>;
          } else {
            return val;
          }
        });

        newObj[key as keyof typeof newObj] = formatPiece;
        return newObj;
      } else {
        newObj[key] = value;
        return newObj;
      }
    },
    {} as OptionType
  );

  return result;
};

export const TagInputWithAutocomplete = forwardRef<
  HTMLUListElement,
  AutocompleteTypes
>(
  (
    {
      items,
      renderItems,
      value: controlledValue = [],
      inputValue = "",
      id,
      open,
      positionFocus,
      onSelectTerm,
      selectedItems,
      ...rest
    },
    containerExternalRef
  ): ReactElement => {
    const containerRef = useRef<HTMLUListElement>(null);
    const { current: uniqueControlId } = useRef(
      uniqueId("suggestion-listbox_")
    );

    const controlId = id || uniqueControlId;
    useCombinedRefs(containerRef, containerExternalRef);

    const listBoxClasses = classnames("spark-auto-suggest__listbox", {
      visible: open && items.length > 0,
    });

    return (
      <ul
        ref={containerRef}
        id={controlId}
        className={listBoxClasses}
        role="listbox"
        {...rest}
      >
        {inputValue.length > 0 &&
          items.map((item: string | Term, index: number) => {
            const isSelected =
              typeof item === "string"
                ? selectedItems?.includes(item)
                : selectedItems?.includes(item.value);

            const termPiece = handleTransformTerm(item as Term, inputValue);
            const stringResult =
              typeof item === "string" &&
              transformItem(item as string, inputValue);

            return (
              <li
                style={{ position: "relative" }}
                role="option"
                aria-selected={isSelected}
                id={"spark-auto-suggest__list-item--" + index}
                className={`spark-auto-suggest__list-item ${
                  index === positionFocus ? "active" : ""
                }`}
                tabIndex={-1}
                key={`list-${index}`}
                onClick={(event): void => onSelectTerm(event, item)}
              >
                {typeof item === "string" && inputValue.length > 0 ? (
                  <div className="spark-listbox-item">
                    <span className="spark-listbox-item__icon">
                      {isSelected && (
                        <Icon size={IconSize.DEFAULT} name="check" />
                      )}
                    </span>
                    <span>
                      {simpleItem(stringResult as string[], inputValue)}
                    </span>
                  </div>
                ) : (
                  renderItems?.(
                    customItem(termPiece as OptionType, inputValue),
                    isSelected
                  )
                )}
              </li>
            );
          })}
      </ul>
    );
  }
);

TagInputWithAutocomplete.displayName = "TagInputWithAutocomplete";
